import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { repairRequestService } from '../../services/repairRequestService';
import { uploadRequestPhotos } from '../../services/requestPhotoStorage';
import type { ServiceProvider } from '../../types/firestore';
import { canCreateRepairRequestForServiceProvider } from '../../utils/accessControl';
import { Input, Button, Alert, Spinner, FormLabel, Modal } from '../common/UIComponents';
import {
  PhotoIcon,
  ExclamationCircleIcon,
  MapPinIcon,
  BanknotesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import {
  getFulfillmentModeLabel,
  resolveServiceFulfillmentCapabilities,
  type FulfillmentMode,
} from '../../shared/fulfillment';
import { REPAIR_REQUEST_PRIORITY_NOTE_MAX_LENGTH } from '../../shared/repairRequestCreatePayload';
import { getFriendlyFirebaseRuntimeError } from '../../utils/firebaseRuntimeError';
import { getServiceProviderProfilePath } from '../../utils/serviceProviderProfilePath';

interface RepairRequestFormData {
  manualDeviceType: string;
  brand?: string;
  model?: string;
  problemDescription: string;
  preferredDate?: string;
  fulfillmentMode: FulfillmentMode;
  fulfillmentAddress?: string;
  fulfillmentNote?: string;
  isPriority: boolean;
  priorityAcceptsPotentialExtraFee: boolean;
  priorityNote?: string;
}

interface RepairRequestFormProps {
  serviceProviderId: string;
  serviceProvider: ServiceProvider;
  demoSubmitMode?: boolean;
  demoSubmitReason?: 'testServiceProvider';
}

type SubmissionResult =
  | {
      kind: 'demo';
      reason: 'testServiceProvider';
    }
  | {
      kind: 'success';
      requestId: string;
    }
  | {
      kind: 'partialPhotoFailure';
      requestId: string;
    };

const MODE_HELPER_TEXT: Record<FulfillmentMode, string> = {
  customerDropoff: 'Dostarczysz sprzęt bezpośrednio do punktu serwisowego.',
  servicePickup: 'Serwis odbierze sprzęt pod wskazanym adresem.',
  onsiteService: 'Serwis wykona wizytę lub diagnozę u Ciebie.',
};

type RequiredSectionId = 'device' | 'fulfillment';
type AttentionCue = 'current' | 'recommended' | 'ready' | 'done';

const getFormSectionStateClass = (cue: AttentionCue | null) => {
  if (cue === 'current') {
    return 'rs-form-section--current';
  }
  if (cue === 'done') {
    return 'rs-form-section--complete rs-form-section--done';
  }
  return '';
};
const FORM_FIELD_CLASS = 'rs-form-field';
const FORM_DATE_FIELD_CLASS = 'rs-form-field rs-form-field--with-action';

const RepairRequestForm: React.FC<RepairRequestFormProps> = ({
  serviceProviderId,
  serviceProvider,
  demoSubmitMode = false,
  demoSubmitReason,
}) => {
  const { currentUser, userProfile, claims, loading: loadingProfile } = useAuth();
  const canSubmitRequest = canCreateRepairRequestForServiceProvider(
    userProfile,
    claims,
    currentUser?.uid || null,
    serviceProviderId
  );
  const fulfillmentCapabilities = useMemo(
    () => resolveServiceFulfillmentCapabilities(serviceProvider),
    [serviceProvider]
  );
  const enabledFulfillmentModes = fulfillmentCapabilities.enabledModes;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<RepairRequestFormData>({
    defaultValues: {
      fulfillmentMode: enabledFulfillmentModes[0],
      isPriority: false,
      priorityAcceptsPotentialExtraFee: false,
      priorityNote: '',
    },
  });

  const navigate = useNavigate();
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);
  const [submissionResult, setSubmissionResult] = useState<SubmissionResult | null>(null);
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);
  const submissionModalTitleId = useId();
  const submissionTriggerRef = useRef<HTMLButtonElement | null>(null);
  const submissionPrimaryActionRef = useRef<HTMLButtonElement | null>(null);
  const submissionInFlightRef = useRef(false);
  const [clientRequestId] = useState(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  });
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const selectedFulfillmentMode = watch('fulfillmentMode');
  const isPriorityRequested = watch('isPriority');
  const manualDeviceTypeValue = watch('manualDeviceType') || '';
  const problemDescriptionValue = watch('problemDescription') || '';
  const fulfillmentAddressValue = watch('fulfillmentAddress') || '';
  const fulfillmentNoteValue = watch('fulfillmentNote') || '';
  const priorityAcceptsPotentialExtraFeeValue = watch('priorityAcceptsPotentialExtraFee');
  const priorityNoteValue = watch('priorityNote') || '';
  const selectedModeCapability = selectedFulfillmentMode
    ? fulfillmentCapabilities.modes[selectedFulfillmentMode]
    : undefined;
  const selectedModeRequiresAddress = selectedModeCapability?.requiresAddress === true;
  const isDeviceSectionComplete =
    manualDeviceTypeValue.trim().length > 0 && problemDescriptionValue.trim().length > 0;
  const isPriorityNoteLengthValid =
    priorityNoteValue.length <= REPAIR_REQUEST_PRIORITY_NOTE_MAX_LENGTH;
  const isPrioritySectionComplete =
    !isPriorityRequested ||
    (priorityAcceptsPotentialExtraFeeValue === true && isPriorityNoteLengthValid);
  const isFulfillmentSectionComplete =
    Boolean(selectedFulfillmentMode) &&
    (!selectedModeRequiresAddress || fulfillmentAddressValue.trim().length > 0);
  const isFulfillmentNoteLengthValid = fulfillmentNoteValue.length <= 500;
  const firstMissingRequiredSection: RequiredSectionId | null = !isDeviceSectionComplete
    ? 'device'
    : !isFulfillmentSectionComplete
      ? 'fulfillment'
      : null;
  const missingRequiredItems = [
    ...(manualDeviceTypeValue.trim().length === 0 ? ['Typ sprzętu'] : []),
    ...(problemDescriptionValue.trim().length === 0 ? ['Opis problemu'] : []),
    ...(!selectedFulfillmentMode ? ['Sposób realizacji'] : []),
    ...(selectedModeRequiresAddress && fulfillmentAddressValue.trim().length === 0
      ? [selectedFulfillmentMode === 'onsiteService' ? 'Adres miejsca wizyty' : 'Adres odbioru']
      : []),
    ...(isPriorityRequested && !priorityAcceptsPotentialExtraFeeValue
      ? ['Potwierdzenie możliwej wyższej ceny za pilność']
      : []),
  ];
  const invalidOptionalItems = [
    ...(!isPriorityNoteLengthValid ? ['Skróć notatkę o pilności'] : []),
    ...(!isFulfillmentNoteLengthValid ? ['Skróć notatkę do dojazdu'] : []),
  ];
  const readinessIssueItems =
    missingRequiredItems.length > 0
      ? missingRequiredItems
      : invalidOptionalItems.length > 0
        ? invalidOptionalItems
        : ['Sprawdź oznaczone pola'];
  const isRequiredIntakeReady =
    firstMissingRequiredSection === null &&
    isPrioritySectionComplete &&
    isPriorityNoteLengthValid &&
    isFulfillmentNoteLengthValid &&
    Object.keys(errors).length === 0;
  const deviceAttentionCue: AttentionCue | null =
    firstMissingRequiredSection === 'device' ? 'current' : isDeviceSectionComplete ? 'done' : null;
  const fulfillmentAttentionCue: AttentionCue | null =
    firstMissingRequiredSection === 'fulfillment'
      ? 'current'
      : isDeviceSectionComplete && isFulfillmentSectionComplete
        ? 'done'
        : null;
  const photosAttentionCue: AttentionCue = selectedPhotos.length > 0 ? 'done' : 'recommended';
  const submitAttentionCue: AttentionCue | null =
    isRequiredIntakeReady && !loading && !createdRequestId ? 'ready' : null;
  const clearSelectedPhotos = () => {
    setSelectedPhotos([]);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };
  const selectedPhotosSummary =
    selectedPhotos.length > 0 ? `Wybrano: ${selectedPhotos.length} / 5` : 'Nie wybrano · maks. 5';
  const serviceAddressText = serviceProvider.address
    ? [
        serviceProvider.address.street,
        serviceProvider.address.postalCode,
        serviceProvider.address.city,
      ]
        .filter(Boolean)
        .join(', ')
    : null;
  const openSubmissionResult = (result: SubmissionResult) => {
    setSubmissionResult(result);
    setIsSubmissionModalOpen(true);
  };
  const handleCloseSubmissionModal = () => setIsSubmissionModalOpen(false);
  const handleOpenCreatedRequest = () => {
    if (!createdRequestId) {
      return;
    }
    navigate(`/app/request/${createdRequestId}`);
  };
  const handlePrimarySubmissionAction = () => {
    if (!submissionResult) {
      return;
    }

    setIsSubmissionModalOpen(false);
    if (submissionResult.kind === 'demo') {
      navigate('/app/discover');
      return;
    }

    navigate(`/app/request/${submissionResult.requestId}`);
  };
  const handleDemoSecondaryAction = () => {
    setIsSubmissionModalOpen(false);
    navigate(getServiceProviderProfilePath(serviceProviderId));
  };

  useEffect(() => {
    if (isPriorityRequested) {
      return;
    }

    setValue('priorityAcceptsPotentialExtraFee', false);
    setValue('priorityNote', '');
    clearErrors(['priorityAcceptsPotentialExtraFee', 'priorityNote']);
  }, [clearErrors, isPriorityRequested, setValue]);

  const onSubmit: SubmitHandler<RepairRequestFormData> = async (data) => {
    if (submissionInFlightRef.current) {
      return;
    }

    if (createdRequestId) {
      handleOpenCreatedRequest();
      return;
    }

    if (!currentUser) {
      setError('Musisz być zalogowany, aby zgłosić naprawę.');
      return;
    }

    if (!canSubmitRequest) {
      setError(
        'Zgłoszenie może utworzyć aktywny klient, administrator albo aktywne konto serwisowe dla innego serwisu.'
      );
      return;
    }

    setError(null);

    const normalizedManualDeviceType = data.manualDeviceType.trim();
    const normalizedBrand = data.brand?.trim() || undefined;
    const normalizedModel = data.model?.trim() || undefined;
    const problemDescription = data.problemDescription.trim();
    const selectedMode = data.fulfillmentMode;
    const capability = fulfillmentCapabilities.modes[selectedMode];
    const normalizedFulfillmentAddress = data.fulfillmentAddress?.trim() || undefined;
    const normalizedFulfillmentNote = data.fulfillmentNote?.trim() || undefined;
    const normalizedPriorityNote = data.priorityNote?.trim() || undefined;
    const priorityRequest = data.isPriority
      ? {
          requested: true,
          acceptsPotentialExtraFee: data.priorityAcceptsPotentialExtraFee === true,
          ...(normalizedPriorityNote ? { note: normalizedPriorityNote } : {}),
        }
      : undefined;

    if (
      fulfillmentCapabilities.state !== 'available' ||
      !capability?.enabled ||
      !enabledFulfillmentModes.includes(selectedMode)
    ) {
      setError('Ten serwis nie ma dostępnej metody realizacji zlecenia.');
      return;
    }

    if (capability.requiresAddress && !normalizedFulfillmentAddress) {
      setError('Podaj adres lub miejsce realizacji dla wybranego sposobu zlecenia.');
      return;
    }

    if (demoSubmitMode) {
      openSubmissionResult({
        kind: 'demo',
        reason: demoSubmitReason || 'testServiceProvider',
      });
      return;
    }

    submissionInFlightRef.current = true;
    setLoading(true);
    const requestData = {
      clientId: currentUser.uid,
      clientRequestId,
      serviceProviderId,
      manualDeviceType: normalizedManualDeviceType,
      ...(normalizedBrand ? { brand: normalizedBrand } : {}),
      ...(normalizedModel ? { model: normalizedModel } : {}),
      description: problemDescription,
      urgency: 'medium' as const,
      scheduledDate: data.preferredDate ? new Date(data.preferredDate) : null,
      clientDetails: {
        displayName:
          userProfile?.displayName || currentUser.displayName || currentUser.email?.split('@')[0],
        email: currentUser.email || userProfile?.email,
      },
      fulfillmentSelection: {
        selectedMode,
        selectedModeSource: capability.source,
        ...(normalizedFulfillmentAddress
          ? {
              address: {
                formatted: normalizedFulfillmentAddress,
              },
            }
          : {}),
        ...(normalizedFulfillmentNote ? { note: normalizedFulfillmentNote } : {}),
      },
      ...(priorityRequest ? { priorityRequest } : {}),
    };

    let newRequestId: string | null = null;
    try {
      newRequestId = await repairRequestService.createRepairRequest(requestData);
      setCreatedRequestId(newRequestId);

      const photosToUpload = selectedPhotos.filter((photo) => photo.size > 0);
      if (photosToUpload.length > 0) {
        const photoAssets = await uploadRequestPhotos(newRequestId, photosToUpload);
        if (photoAssets.length > 0) {
          await repairRequestService.attachRequestPhotos(newRequestId, photoAssets);
        }
      }
      reset();
      clearSelectedPhotos();
      openSubmissionResult({
        kind: 'success',
        requestId: newRequestId,
      });
    } catch (err: any) {
      const message = getFriendlyFirebaseRuntimeError(err, err?.message || 'Nieznany błąd');
      if (newRequestId) {
        console.error('Zgłoszenie utworzone, ale nie udało się dołączyć zdjęć:', err);
        clearSelectedPhotos();
        openSubmissionResult({
          kind: 'partialPhotoFailure',
          requestId: newRequestId,
        });
        return;
      }

      console.error('Błąd wysyłania zgłoszenia:', err);
      setError(message);
    } finally {
      submissionInFlightRef.current = false;
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="flex justify-center items-center p-6">
        <Spinner size="md" />
        <p className="ml-3 text-primary-light-300">Sprawdzanie uprawnień...</p>
      </div>
    );
  }

  if (!currentUser || !canSubmitRequest) {
    return (
      <Alert variant="error" className="mt-4">
        Musisz mieć aktywne konto klienta, administratora albo konto serwisowe, aby zgłosić naprawę.
      </Alert>
    );
  }

  if (fulfillmentCapabilities.state !== 'available' || enabledFulfillmentModes.length === 0) {
    return (
      <Alert variant="error" className="mt-4">
        Ten serwis nie ma obecnie dostępnej metody realizacji zlecenia. Wybierz inny serwis albo
        wróć później.
      </Alert>
    );
  }

  return (
    <>
      <aside
        className="rs-form-context-panel"
        aria-label="Kontekst zgłoszenia"
        data-testid="create-request-context-panel"
      >
        <section className="rs-form-service-context" data-testid="create-request-service-context">
          <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-ratuj-300" aria-hidden="true" />
          <div className="min-w-0">
            <p className="rs-form-context-label">Wybrany serwis</p>
            <h2 className="rs-form-service-name">{serviceProvider.serviceName}</h2>
            {serviceAddressText && <p className="rs-form-service-address">{serviceAddressText}</p>}
          </div>
        </section>

        <div className="rs-form-context-details">
          <section
            className={`rs-form-context-section rs-form-context-state ${
              isRequiredIntakeReady ? 'rs-form-context-state--ready' : ''
            }`}
            aria-live="polite"
            data-testid="request-readiness-summary"
            data-readiness-state={isRequiredIntakeReady ? 'ready' : 'incomplete'}
          >
            <div className="flex items-start gap-2">
              {isRequiredIntakeReady ? (
                <CheckCircleIcon
                  className="mt-0.5 h-4 w-4 shrink-0 text-ratuj-300"
                  aria-hidden="true"
                />
              ) : (
                <InformationCircleIcon
                  className="mt-0.5 h-4 w-4 shrink-0 text-primary-light-300"
                  aria-hidden="true"
                />
              )}
              <div className="min-w-0">
                <p className="rs-form-context-heading">
                  {isRequiredIntakeReady
                    ? 'Gotowe do wysłania'
                    : missingRequiredItems.length > 0
                      ? 'Brakuje do wysłania'
                      : 'Popraw przed wysłaniem'}
                </p>
                {isRequiredIntakeReady ? (
                  <p className="rs-form-context-copy mt-1">
                    Wszystkie wymagane informacje są uzupełnione.
                  </p>
                ) : (
                  <ul className="rs-form-missing-list">
                    {readinessIssueItems.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          <section className="rs-form-context-section">
            <p className="rs-form-context-heading">Opcjonalne informacje</p>
            <p className="rs-form-context-copy mt-1">
              Zdjęcia, termin i oznaczenie pilności są opcjonalne.
            </p>
          </section>

          <section className="rs-form-context-section">
            <p className="rs-form-context-heading">Po wysłaniu</p>
            <p className="rs-form-context-copy mt-1">
              Zgłoszenie pojawi się w Twoich zleceniach. Tam odbędzie się dalsza rozmowa i śledzenie
              statusu.
            </p>
          </section>

          <footer className="rs-form-trust-note flex items-start gap-2">
            <ShieldCheckIcon
              className="mt-0.5 h-4 w-4 shrink-0 text-primary-light-300"
              aria-hidden="true"
            />
            <span>
              Dane widzą tylko uczestnicy sprawy i administratorzy zgodnie z regułami dostępu.
            </span>
          </footer>
        </div>
      </aside>

      <form onSubmit={handleSubmit(onSubmit)} className="rs-form-main">
        {error && (
          <div className="rs-form-error-summary" role="alert">
            <Alert variant="error" icon={<ExclamationCircleIcon className="w-5 h-5" />}>
              {error}
            </Alert>
          </div>
        )}

        <section
          className={`rs-form-section ${getFormSectionStateClass(deviceAttentionCue)} space-y-3.5`}
          data-testid="request-section-device"
          data-attention-cue={deviceAttentionCue || 'none'}
          data-required-state={isDeviceSectionComplete ? 'complete' : 'incomplete'}
          aria-current={firstMissingRequiredSection === 'device' ? 'step' : undefined}
        >
          <div className="rs-form-section-header">
            <span className="rs-form-section-number" aria-hidden="true">
              1
            </span>
            <div className="min-w-0">
              <h2 className="rs-form-section-title">Sprzęt i problem</h2>
              <p className="rs-form-section-copy">Podaj sprzęt i opisz, co się z nim dzieje.</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Input
              id="manualDeviceType"
              label="Typ sprzętu"
              placeholder="np. laptop, pralka, telefon"
              error={errors.manualDeviceType?.message}
              className={`${FORM_FIELD_CLASS} ${
                errors.manualDeviceType ? 'rs-form-field--error' : ''
              }`}
              {...register('manualDeviceType', {
                required: 'Typ sprzętu jest wymagany',
                setValueAs: (value) =>
                  typeof value === 'string' ? value.trim() : String(value || '').trim(),
              })}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              id="brand"
              label="Marka (opcjonalnie)"
              placeholder="np. Bosch, Dell, Samsung"
              className={FORM_FIELD_CLASS}
              {...register('brand')}
            />
            <Input
              id="model"
              label="Model (opcjonalnie)"
              className={FORM_FIELD_CLASS}
              {...register('model')}
            />
          </div>

          <div>
            <FormLabel required>Opis problemu</FormLabel>
            <textarea
              id="problemDescription"
              rows={3}
              aria-invalid={errors.problemDescription ? 'true' : 'false'}
              className={`rs-form-field rs-form-field--textarea rs-form-field--description mt-1 block ${
                errors.problemDescription ? 'rs-form-field--error' : ''
              }`}
              placeholder="Co się dzieje, od kiedy, czy sprzęt był już naprawiany?"
              {...register('problemDescription', {
                required: 'Opis problemu jest wymagany',
              })}
            />
            {errors.problemDescription && (
              <p className="form-error">{errors.problemDescription.message}</p>
            )}
          </div>
        </section>

        <section
          className={`rs-form-section ${getFormSectionStateClass(
            fulfillmentAttentionCue
          )} space-y-3`}
          data-testid="request-section-fulfillment"
          data-attention-cue={fulfillmentAttentionCue || 'none'}
          data-required-state={isFulfillmentSectionComplete ? 'complete' : 'incomplete'}
          aria-current={firstMissingRequiredSection === 'fulfillment' ? 'step' : undefined}
        >
          <div className="rs-form-section-header">
            <span className="rs-form-section-number" aria-hidden="true">
              2
            </span>
            <div className="min-w-0">
              <FormLabel
                required
                icon={<MapPinIcon className="h-4 w-4 text-primary-light-400/80" />}
                className="rs-form-section-title"
              >
                Sposób realizacji
              </FormLabel>
              <p className="rs-form-section-copy">
                Wybierz jedną z metod dostępnych w tym serwisie.
              </p>
            </div>
          </div>

          <div className="space-y-2" role="radiogroup" aria-label="Sposób realizacji">
            {enabledFulfillmentModes.map((mode) => {
              const isSelected = selectedFulfillmentMode === mode;
              const capability = fulfillmentCapabilities.modes[mode];

              return (
                <label
                  key={mode}
                  className={`rs-form-option ${isSelected ? 'rs-form-option--selected' : ''}`}
                  data-testid={`request-fulfillment-option-${mode}`}
                  data-selected={isSelected ? 'true' : 'false'}
                >
                  <input
                    type="radio"
                    value={mode}
                    className="sr-only"
                    {...register('fulfillmentMode', {
                      required: 'Wybierz sposób realizacji zlecenia',
                    })}
                  />
                  <span className="rs-form-option-indicator" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="rs-form-option-title">{getFulfillmentModeLabel(mode)}</span>
                    <span className="rs-form-option-description">
                      {capability.instructions || MODE_HELPER_TEXT[mode]}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {errors.fulfillmentMode && <p className="form-error">{errors.fulfillmentMode.message}</p>}

          {selectedModeRequiresAddress && (
            <div className="space-y-3">
              <Input
                id="fulfillmentAddress"
                label={
                  selectedFulfillmentMode === 'onsiteService'
                    ? 'Adres / miejsce wizyty'
                    : 'Adres / miejsce odbioru'
                }
                placeholder="np. Testowa 12/4, 00-001 Warszawa"
                error={errors.fulfillmentAddress?.message}
                className={`${FORM_FIELD_CLASS} ${
                  errors.fulfillmentAddress ? 'rs-form-field--error' : ''
                }`}
                {...register('fulfillmentAddress', {
                  validate: (value) =>
                    !selectedModeRequiresAddress ||
                    Boolean(value?.trim()) ||
                    'Podaj adres lub miejsce realizacji',
                })}
              />
              <div>
                <label
                  htmlFor="fulfillmentNote"
                  className="flex items-center text-sm font-medium text-primary-light-200"
                >
                  Notatka do dojazdu (opcjonalnie)
                </label>
                <textarea
                  id="fulfillmentNote"
                  rows={2}
                  aria-invalid={errors.fulfillmentNote ? 'true' : 'false'}
                  className={`rs-form-field rs-form-field--textarea rs-form-field--travel-note mt-1 block ${
                    errors.fulfillmentNote ? 'rs-form-field--error' : ''
                  }`}
                  placeholder="np. piętro, domofon, preferowane godziny"
                  {...register('fulfillmentNote', {
                    maxLength: {
                      value: 500,
                      message: 'Notatka może mieć maksymalnie 500 znaków',
                    },
                  })}
                />
                {errors.fulfillmentNote && (
                  <p className="form-error">{errors.fulfillmentNote.message}</p>
                )}
              </div>
            </div>
          )}

          <div
            className="rs-form-payment-meta flex items-start gap-2 text-xs leading-snug text-primary-light-300"
            data-testid="request-payment-meta"
          >
            <BanknotesIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary-light-400/85" />
            <span>Płatność: gotówka / rozliczenie bezpośrednie z serwisem.</span>
          </div>
        </section>

        <section
          className="rs-form-section rs-form-section--optional"
          data-testid="request-section-additional"
        >
          <div className="rs-form-section-header">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="rs-form-section-title">Dodatkowe informacje</h2>
                <span className="rs-form-status-pill">Opcjonalne</span>
              </div>
              <p className="rs-form-section-copy">Pilność, termin i zdjęcia nie są wymagane.</p>
            </div>
          </div>

          <div className="rs-form-optional-grid">
            <div className="rs-form-priority-block" data-testid="priority-request-row">
              <label
                htmlFor="isPriority"
                className={`rs-form-checkbox-row flex cursor-pointer items-start gap-3 transition-colors ${
                  isPriorityRequested ? 'text-primary-light-100' : 'text-primary-light-200'
                }`}
              >
                <input
                  id="isPriority"
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-200 text-status-warning-400 focus:ring-status-warning-400 focus:ring-offset-primary-dark-100"
                  {...register('isPriority')}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold leading-snug">
                    Potrzebujesz szybkiej reakcji?
                  </span>
                  <span className="mt-0.5 block text-xs leading-snug text-primary-light-400">
                    Oznacz zgłoszenie jako pilne tylko wtedy, gdy czas ma duże znaczenie.
                  </span>
                </span>
              </label>

              {isPriorityRequested && (
                <div className="rs-form-priority-details space-y-3">
                  <label
                    htmlFor="priorityAcceptsPotentialExtraFee"
                    className="flex cursor-pointer items-start gap-3 rounded-md"
                  >
                    <input
                      id="priorityAcceptsPotentialExtraFee"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-primary-dark-300 bg-primary-dark-200 text-status-warning-400 focus:ring-status-warning-400 focus:ring-offset-primary-dark-100"
                      {...register('priorityAcceptsPotentialExtraFee', {
                        validate: (value) =>
                          !isPriorityRequested || value || 'Potwierdź możliwość wyższej ceny.',
                      })}
                    />
                    <span className="min-w-0 flex-1 text-sm leading-snug text-primary-light-200">
                      Rozumiem, że serwis może zaproponować wyższą cenę za priorytet.
                    </span>
                  </label>
                  {errors.priorityAcceptsPotentialExtraFee && (
                    <p className="form-error">{errors.priorityAcceptsPotentialExtraFee.message}</p>
                  )}

                  <div>
                    <label
                      htmlFor="priorityNote"
                      className="flex items-center text-sm font-medium text-primary-light-200"
                    >
                      Co jest najpilniejsze?
                    </label>
                    <textarea
                      id="priorityNote"
                      rows={2}
                      aria-invalid={errors.priorityNote ? 'true' : 'false'}
                      className={`rs-form-field rs-form-field--textarea rs-form-field--short-note mt-1 block ${
                        errors.priorityNote ? 'rs-form-field--error' : ''
                      }`}
                      placeholder="Np. potrzebuję sprzętu do pracy jutro rano"
                      {...register('priorityNote', {
                        maxLength: {
                          value: REPAIR_REQUEST_PRIORITY_NOTE_MAX_LENGTH,
                          message: `Notatka może mieć maksymalnie ${REPAIR_REQUEST_PRIORITY_NOTE_MAX_LENGTH} znaków`,
                        },
                      })}
                    />
                    {errors.priorityNote ? (
                      <p className="form-error">{errors.priorityNote.message}</p>
                    ) : (
                      <p className="mt-1 text-xs leading-snug text-primary-light-400">
                        Krótko wskaż, co wymaga najszybszej reakcji.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="rs-form-optional-date">
              <Input
                id="preferredDate"
                label="Preferowana data kontaktu/naprawy"
                type="date"
                placeholder="dd/mm/rrrr"
                className={FORM_DATE_FIELD_CLASS}
                {...register('preferredDate')}
              />
              <p className="mt-1 text-xs leading-snug text-primary-light-400">
                To preferencja dla serwisu, nie potwierdzony termin naprawy.
              </p>
            </div>
          </div>

          <div className="rs-form-photo-group">
            <h3 className="rs-form-photo-heading">
              <PhotoIcon
                className="h-4 w-4 shrink-0 text-primary-light-400/80"
                aria-hidden="true"
              />
              <span>
                Zdjęcia <span className="rs-form-photo-recommendation">— zalecane</span>
              </span>
            </h3>
            <p className="rs-form-section-copy">Dodaj tabliczkę znamionową lub widoczną usterkę.</p>

            <input
              ref={photoInputRef}
              id="requestPhotos"
              type="file"
              aria-label="Dodaj zdjęcia"
              accept="image/*"
              multiple
              disabled={loading}
              className="sr-only"
              onChange={(event) => {
                const files = Array.from(event.target.files || []).slice(0, 5);
                setSelectedPhotos(files);
              }}
            />
            <div
              className="rs-form-upload-row flex-col items-stretch sm:flex-row sm:items-center"
              data-testid="request-photo-picker"
              data-attention-cue={photosAttentionCue}
              data-photo-state={selectedPhotos.length > 0 ? 'selected' : 'empty'}
            >
              <Button
                type="button"
                variant="neutral"
                size="sm"
                disabled={loading}
                onClick={() => photoInputRef.current?.click()}
                className="w-full shrink-0 sm:w-auto"
              >
                Dodaj zdjęcia
              </Button>
              <div className="min-w-0 flex-1 space-y-0.5">
                <p
                  className="truncate text-sm text-primary-light-200"
                  data-testid="request-photo-picker-summary"
                >
                  {selectedPhotosSummary}
                </p>
                <p className="text-xs leading-snug text-primary-light-400">
                  {selectedPhotos.length > 0
                    ? 'Zdjęcia zostaną dołączone po utworzeniu zgłoszenia.'
                    : 'Zdjęcia nie są wymagane.'}
                </p>
              </div>
              {selectedPhotos.length > 0 && (
                <Button
                  type="button"
                  variant="neutral"
                  size="sm"
                  disabled={loading}
                  onClick={clearSelectedPhotos}
                  className="w-full shrink-0 sm:w-auto"
                >
                  Usuń wybór
                </Button>
              )}
            </div>
          </div>
        </section>

        <div
          className="rs-form-submit-bar"
          data-testid="request-submit-region"
          data-attention-cue={submitAttentionCue || 'none'}
          data-required-state={isRequiredIntakeReady ? 'complete' : 'incomplete'}
        >
          <div
            className="rs-form-submit-readiness"
            aria-live="polite"
            data-testid="request-submit-readiness"
          >
            <p className="text-sm font-semibold leading-snug text-primary-light-100">
              {isRequiredIntakeReady
                ? 'Gotowe do wysłania'
                : missingRequiredItems.length > 0
                  ? 'Brakuje do wysłania'
                  : 'Popraw przed wysłaniem'}
            </p>
            <p className="rs-form-submit-meta">
              {isRequiredIntakeReady
                ? 'Sprawdź dane i wyślij zgłoszenie.'
                : `${missingRequiredItems.length > 0 ? 'Uzupełnij' : 'Popraw'}: ${readinessIssueItems.join(
                    ', '
                  )}.`}
            </p>
          </div>
          <Button
            ref={submissionTriggerRef}
            data-testid="request-submit-button"
            data-attention-cue={submitAttentionCue || 'none'}
            type={createdRequestId ? 'button' : 'submit'}
            variant="primary"
            fullWidth
            disabled={loading}
            onClick={createdRequestId ? handleOpenCreatedRequest : undefined}
            className="rs-form-primary-submit text-base font-semibold"
          >
            {loading ? (
              <>
                <Spinner size="sm" color="white" className="mr-2" />
                Wysyłanie...
              </>
            ) : createdRequestId ? (
              'Otwórz utworzone zgłoszenie'
            ) : (
              'Wyślij zgłoszenie'
            )}
          </Button>
        </div>
      </form>

      <Modal
        isOpen={isSubmissionModalOpen && submissionResult !== null}
        onClose={handleCloseSubmissionModal}
        ariaLabelledBy={submissionModalTitleId}
        initialFocusRef={submissionPrimaryActionRef}
        returnFocusRef={submissionTriggerRef}
        className="max-w-lg"
      >
        {submissionResult && (
          <div className="px-4 py-4 sm:px-6 sm:py-5">
            <div className="space-y-5">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.22em] text-primary-light-400">
                  {submissionResult.kind === 'demo'
                    ? 'Tryb demonstracyjny'
                    : 'Potwierdzenie zgłoszenia'}
                </p>
                <h2
                  id={submissionModalTitleId}
                  className="text-lg font-semibold text-primary-light-100 sm:text-xl"
                >
                  {submissionResult.kind === 'demo'
                    ? 'To jest serwis testowy'
                    : submissionResult.kind === 'partialPhotoFailure'
                      ? 'Zgłoszenie utworzone'
                      : 'Zgłoszenie wysłane'}
                </h2>
                <div className="space-y-2 text-sm leading-relaxed text-primary-light-300">
                  {submissionResult.kind === 'demo' ? (
                    <>
                      <p>
                        Formularz działa demonstracyjnie. Tego zgłoszenia nie wysłaliśmy i nie trafi
                        ono do żadnego serwisu.
                      </p>
                      <p className="text-primary-light-400">
                        Wybierz prawdziwy serwis, żeby zlecić naprawę.
                      </p>
                    </>
                  ) : submissionResult.kind === 'partialPhotoFailure' ? (
                    <>
                      <p>Zgłoszenie zostało wysłane, ale nie udało się dołączyć części zdjęć.</p>
                      <p className="text-primary-light-400">
                        Status, rozmowę i dalsze decyzje znajdziesz w karcie zlecenia.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        Dziękujemy. Serwis otrzymał zgłoszenie i może poprosić o dodatkowe
                        informacje albo przygotować wycenę.
                      </p>
                      <p className="text-primary-light-400">
                        Status, rozmowę i dalsze decyzje znajdziesz w karcie zlecenia.
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 pb-[calc(0.25rem+env(safe-area-inset-bottom))] sm:flex-row-reverse">
                <Button
                  ref={submissionPrimaryActionRef}
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={handlePrimarySubmissionAction}
                  icon={<ArrowRightIcon className="h-4 w-4" />}
                  iconPosition="right"
                  className="sm:flex-1"
                >
                  {submissionResult.kind === 'demo'
                    ? 'Znajdź prawdziwy serwis'
                    : 'Przejdź do zlecenia'}
                </Button>

                {submissionResult.kind === 'demo' && (
                  <Button
                    type="button"
                    variant="neutral"
                    fullWidth
                    onClick={handleDemoSecondaryAction}
                    className="sm:flex-1"
                  >
                    Wróć do profilu
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RepairRequestForm;
